/**
 * Displacement Radar v2 — pure helpers.
 *
 * Transport lives in `@/lib/api/procurementServer` (fetchCategoryIntel) so this
 * page uses the same credentialed call as every other procurement endpoint.
 * A bespoke fetch here previously omitted `credentials: 'include'` and the
 * API's tenant gate answered 403 before routing.
 */

export const WINDOW_LABELS = ['0\u20136 mo', '6\u201312 mo', '12\u201324 mo', '24+ mo', 'No end date'];

export const SENTIMENT_ROWS = [
  { key: 'neg', label: 'Negative', tone: 'hot' },
  { key: 'neu', label: 'Neutral', tone: 'warn' },
  { key: 'pos', label: 'Positive', tone: 'good' },
];

/**
 * Matrix cells arrive as {neg:[5],neu:[5],pos:[5]} — four decision windows plus
 * a column for awards with no recorded end date. Padded rather than
 * length-checked so a cached four-element payload still renders.
 */
export function matrixRow(matrix, key) {
  const row = matrix && matrix[key];
  if (!Array.isArray(row)) return [0, 0, 0, 0, 0];
  return [0, 1, 2, 3, 4].map((i) => Number(row[i]) || 0);
}

/**
 * Share of live contracts whose incumbent position is known. Ranges from ~2%
 * (hardware) to ~72% (cloud), so the page shows it rather than letting a
 * near-empty grid read as "no risk".
 */
export function coveragePct(row) {
  if (!row?.live_contracts) return 0;
  return Math.round((row.scored_contracts / row.live_contracts) * 100);
}

/**
 * Incumbents expected to actually lose an account among those expiring inside
 * six months: the churn rate pro-rated to the half year. NOT the share of next
 * awards going to a different supplier, which runs 70-85% because buyers hold
 * several suppliers at once and is not a loss.
 */
export function expectedLosses(row) {
  const churn = Number(row?.churn_established) || 0;
  return Math.round(((row?.w0_6 || 0) * churn) / 100 / 2);
}

/** Compact USD, e.g. $1.6bn / $201.9m / $420k. Input is millions. */
export function fmtMusd(musd) {
  const n = Number(musd) || 0;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}bn`;
  if (n >= 1) return `$${n.toFixed(1)}m`;
  if (n > 0) return `$${Math.round(n * 1000)}k`;
  return '—';
}

/** Same, from a raw USD figure. */
export function fmtUsd(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '—';
  return fmtMusd(n / 1e6);
}
